/**
 * One catalog, shared by every surface that needs it (RESP2d).
 *
 * ## Why a module-level store and not another hook-with-a-fetch
 *
 * The repo's existing pattern (`useResponsibilityAreas`, `useAreaUsage`)
 * is a hook that owns its own request. That is right for a hook with ONE
 * consumer — a settings page, a detail panel. It is wrong here: the
 * catalog is read by both form modals, both detail pages, the workspace
 * cards and every chip in an entity overview, and a per-consumer fetch
 * would mean a request per rendered row. The requirement is explicitly
 * one request per page, never one per task.
 *
 * There is no React Query in this codebase and RESP2d does not add one —
 * a data-fetching framework is a large decision to smuggle in as a side
 * effect of a category dropdown. So this is the smallest thing that
 * satisfies the requirement: a module-level cache, an in-flight promise
 * so concurrent mounts share one request, and a subscriber list so every
 * consumer re-renders when it lands.
 *
 * ## Why invalidation is a function and not a convention
 *
 * Renaming an area in RESP2c has to change what a task list shows. With
 * a per-hook fetch that would mean "remember to refresh six places";
 * here it is one call, and `resp2c` mutations make it. A cache that can
 * only be corrected by reloading the page is a cache that will be wrong.
 *
 * ## What it deliberately does not do
 *
 * No polling, no TTL, no background revalidation. The catalog changes
 * when somebody edits it, and that somebody is in this tab — so an
 * explicit invalidation covers every case that actually happens. A TTL
 * would add a class of "stale for up to N seconds" bugs to buy nothing.
 */
// Imported from the client module DIRECTLY, not from the ../adminData
// barrel. `src/setupTests.js` seeds this store for every test, so the
// barrel would be evaluated before any test file runs — and a suite that
// does `jest.mock("./core")` would then find its module already built
// against the real core. One narrow import instead of the whole API
// surface keeps that from happening.
import { fetchActivityCategoryOptions } from "../../../api/adminData/activityCategories";

/** `idle` → `loading` → (`ready` | `error`). */
const initialState = {
  status: "idle",
  items: [],
  error: null,
};

let state = initialState;
let inflight = null;
const listeners = new Set();

function setState(next) {
  state = next;
  for (const l of Array.from(listeners)) l(state);
}

export function getCatalogState() {
  return state;
}

export function subscribeToCatalog(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Fetch once; concurrent callers share the same promise.
 *
 * `force` is what invalidation uses — without it a second call while
 * `status === "ready"` is a no-op, which is the whole point.
 */
export function loadCatalog({ force = false } = {}) {
  if (!force && (state.status === "ready" || state.status === "loading")) {
    return inflight || Promise.resolve(state);
  }
  setState({ ...state, status: "loading", error: null });
  inflight = fetchActivityCategoryOptions()
    .then((data) => {
      const items = Array.isArray(data?.items) ? data.items : [];
      setState({ status: "ready", items, error: null });
      return state;
    })
    .catch((err) => {
      setState({
        // Items are kept on purpose: a refresh that fails should not
        // blank a dropdown the person is looking at.
        ...state,
        status: "error",
        error:
          err?.message || "Tätigkeitsbereiche konnten nicht geladen werden.",
      });
      return state;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Throw the catalog away and fetch it again.
 *
 * Called by every RESP2c mutation — create, rename, activate,
 * deactivate, delete — because each of them changes what a task form may
 * offer or what a task list must call an area.
 */
export function invalidateActivityCategoryCatalog() {
  return loadCatalog({ force: true });
}

/** Test-only: the module-level cache would otherwise leak across tests. */
export function __resetActivityCategoryCatalog() {
  state = initialState;
  inflight = null;
  listeners.clear();
}

/**
 * Test-only: put a catalog in place without a request.
 *
 * Marks the store `ready`, so `loadCatalog()` short-circuits and no
 * component reaches for the API at all. That is what lets a test about
 * task ROWS stay a test about task rows — before this, every suite that
 * rendered a category anywhere would have had to grow an HTTP mock for
 * a dependency it does not care about.
 */
export function __seedActivityCategoryCatalog(items) {
  setState({ status: "ready", items: items || [], error: null });
  inflight = null;
}
